/**
 * sl-publish-preconfig.L3: ExtractedVarsEditor
 *
 * Publisher-dialog surface for the parameterized config variables the
 * sanitizer extracted from AI prompts, HTTP body/headers, and URLs. Each
 * row lets the author rename the variable (label), describe it, and flip
 * the required flag. The editor is pure-controlled — the parent owns the
 * override map and receives patches via `onChange`.
 *
 * SOLID:
 *   - S: only renders / edits the extracted variables
 *   - D: depends on ParameterizedValue shape (backend SSOT via
 *        `/marketplace/analyze` response)
 */
import { useCallback } from 'react';

import * as styles from './styles.css';

export interface ParameterizedValue {
  originalValue: string;
  placeholder: string;
  configKey: string;
  stepId: string;
  autoDescription: string;
  userDescription?: string;
  userConfigKey?: string;
  required?: boolean;
}

export interface ExtractedVarsEditorProps {
  values: readonly ParameterizedValue[];
  overrides: Readonly<Record<string, Partial<ParameterizedValue>>>;
  onChange: (configKey: string, patch: Partial<ParameterizedValue>) => void;
}

export function ExtractedVarsEditor({
  values,
  overrides,
  onChange,
}: ExtractedVarsEditorProps) {
  if (values.length === 0) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <div className={styles.detailSectionTitle}>Configurable Variables</div>
      <div style={{ fontSize: '12px', opacity: 0.7 }}>
        Installers will fill in these values. Edit the label and description to
        explain what each variable is for.
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
      >
        {values.map(v => (
          <VarRow
            key={`${v.stepId}:${v.configKey}`}
            value={v}
            override={overrides[v.configKey]}
            onChange={onChange}
          />
        ))}
      </div>
    </div>
  );
}

function VarRow({
  value,
  override,
  onChange,
}: {
  value: ParameterizedValue;
  override: Partial<ParameterizedValue> | undefined;
  onChange: (configKey: string, patch: Partial<ParameterizedValue>) => void;
}) {
  const effectiveLabel = override?.userConfigKey ?? value.userConfigKey ?? '';
  const effectiveDescription =
    override?.userDescription ?? value.userDescription ?? value.autoDescription;
  const effectiveRequired = override?.required ?? value.required ?? true;

  const patch = useCallback(
    (p: Partial<ParameterizedValue>) => onChange(value.configKey, p),
    [onChange, value.configKey]
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        padding: '10px 12px',
        borderRadius: '6px',
        border: '1px solid color-mix(in srgb, currentColor 12%, transparent)',
        background: 'color-mix(in srgb, currentColor 4%, transparent)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
        }}
      >
        <code style={{ fontSize: '11px', opacity: 0.7 }}>
          {value.placeholder}
        </code>
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '11px',
            opacity: 0.8,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={effectiveRequired}
            onChange={e => patch({ required: e.target.checked })}
          />
          required
        </label>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <label style={{ fontSize: '11px', fontWeight: 500, opacity: 0.75 }}>
          Label
        </label>
        <input
          className={styles.searchInput}
          type="text"
          value={effectiveLabel}
          placeholder={value.configKey}
          onChange={e => patch({ userConfigKey: e.target.value })}
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <label style={{ fontSize: '11px', fontWeight: 500, opacity: 0.75 }}>
          Description
        </label>
        <textarea
          className={styles.searchInput}
          value={effectiveDescription}
          onChange={e => patch({ userDescription: e.target.value })}
          rows={2}
          style={{ width: '100%', resize: 'vertical' }}
        />
      </div>
    </div>
  );
}
